/**
 * Types for the Website Knowledge Crawler.
 *
 * These mirror the GRMS Spring contract exposed at /api/v1/chat/website-crawls,
 * NOT the Python crawler's own snake_case schema. Spring performs the
 * snake_case -> camelCase mapping and strips crawler filesystem paths, so the
 * browser only ever sees the shapes declared here.
 */

/**
 * Normalized crawl status. Spring upper-cases the crawler's lowercase status,
 * so these are the only values the frontend receives. Note that the Python
 * crawler has no "completed with errors" or "cancelled" state; a crawl that
 * stops early reports INTERRUPTED.
 */
export type WebsiteCrawlStatus =
  | 'QUEUED'
  | 'RUNNING'
  | 'COMPLETED'
  | 'FAILED'
  | 'INTERRUPTED';

/** Statuses that mean the crawl is finished and polling must stop. */
export const TERMINAL_CRAWL_STATUSES: readonly WebsiteCrawlStatus[] = [
  'COMPLETED',
  'FAILED',
  'INTERRUPTED'
];

/** Scope values accepted by Spring's WebsiteCrawlScopeMode enum. */
export type WebsiteCrawlScopeMode =
  | 'EXACT_ORIGIN'
  | 'BASE_PATH'
  | 'INCLUDE_SUBDOMAINS';

/** Request body for POST /api/v1/chat/website-crawls. */
export interface WebsiteCrawlRequest {
  baseUrl: string;
  maxPages?: number;
  maxDepth?: number;
  concurrency?: number;
  navigationTimeoutMs?: number;
  waitAfterLoadMs?: number;
  scopeMode?: WebsiteCrawlScopeMode;
  respectRobotsTxt?: boolean;
  saveRenderedHtml?: boolean;
  saveScreenshot?: boolean;
  interactWithCarousels?: boolean;
  maxSlidesPerCarousel?: number;
  slideTransitionWaitMs?: number;
  contentSelector?: string;
  removeSelectors?: string[];
}

/** Response body for POST /api/v1/chat/website-crawls (HTTP 202). */
export interface WebsiteCrawlCreateResponse {
  crawlId: string;
  status: WebsiteCrawlStatus;
  baseUrl: string;
  createdAt: string;
}

/**
 * A single extracted page, forwarded from the crawler's `documents` array.
 * Field names remain snake_case because Spring passes the crawler documents
 * through verbatim (they contain no host paths).
 */
export interface WebsiteCrawlPageResult {
  sequence_number: number;
  original_url: string;
  normalized_url: string;
  final_url: string;
  canonical_url: string | null;
  parent_url: string | null;
  depth: number;
  http_status: number | null;
  title: string | null;
  meta_description: string | null;
  main_heading: string | null;
  language: string | null;
  paragraphs: string[];
  clean_text: string;
  content_hash: string;
  duplicate_content: boolean;
  crawled_at: string;
  duration_ms: number;
  faqs: WebsiteCrawlFaq[];
  links: WebsiteCrawlLink[];
}

export interface WebsiteCrawlLink {
  text: string;
  url: string;
  original_href: string;
  link_type: 'internal' | 'external' | 'email' | 'telephone' | 'fragment' | 'other';
  source_order: number;
}

export interface WebsiteCrawlFaq {
  question: string;
  answer: string;
}

/**
 * Crawler report, available only once the crawl reaches a terminal status.
 * Spring removes `output_directory` from both the report root and
 * `crawl_configuration`, so no crawler-host path is present.
 */
export interface WebsiteCrawlReport {
  base_url?: string;
  normalized_base_url?: string;
  started_at?: string;
  completed_at?: string;
  duration_ms?: number;
  interrupted?: boolean;
  total_discovered_urls?: number;
  total_processed_urls?: number;
  total_successful_urls?: number;
  total_failed_urls?: number;
  total_skipped_urls?: number;
  total_duplicate_urls?: number;
  skip_reason_counts?: Record<string, number>;
  duplicate_reason_counts?: Record<string, number>;
  total_parsed_documents?: number;
  total_documents_parsed?: number;
  total_documents_with_ocr_text?: number;
  total_decorative_images?: number;
  ocr?: {
    images_discovered?: number;
    images_eligible?: number;
    images_processed?: number;
    successful?: number;
    low_confidence?: number;
    failed?: number;
    words_extracted?: number;
    words_included?: number;
  };
  total_ocr_documents_read?: number;
  ocr_documents_read?: number;
  skipped_breakdown?: Record<string, number>;
  skip_breakdown?: Record<string, number>;
  skip_reasons?: Record<string, number>;
  duplicate_breakdown?: Record<string, number>;
  duplicates_breakdown?: Record<string, number>;
  total_paragraphs_extracted?: number;
  total_sections_extracted?: number;
  total_tables_extracted?: number;
  total_faqs_extracted?: number;
  error_summary?: Record<string, number>;
}

/** Optional artifact metadata returned by Spring when the Python crawler exposes it. */
export interface WebsiteCrawlArtifacts {
  documentsJsonlAvailable: boolean | null;
  documentsJsonlFilename?: string | null;
  documentsJsonlRecordCount?: number | null;
  individualPageJsonAvailable?: boolean | null;
  schemaVersion?: string | null;
  generatedAt?: string | null;
  fileSizeBytes?: number | null;
}

/** Response body for GET /api/v1/chat/website-crawls/{crawlId}. */
export interface WebsiteCrawlStatusResponse {
  crawlId: string;
  status: WebsiteCrawlStatus;
  baseUrl: string;
  normalizedUrl: string;
  currentUrl: string | null;
  discoveredCount: number | null;
  queuedCount: number | null;
  processedCount: number | null;
  successfulCount: number | null;
  failedCount: number | null;
  skippedCount: number | null;
  duplicateCount: number | null;
  documentCount: number | null;
  parsedDocumentCount?: number | null;
  ocrReadCount?: number | null;
  ocrDocumentCount?: number | null;
  skippedBreakdown?: Record<string, number> | null;
  skipBreakdown?: Record<string, number> | null;
  skipReasons?: Record<string, number> | null;
  duplicateBreakdown?: Record<string, number> | null;
  duplicatesBreakdown?: Record<string, number> | null;
  sectionCount: number | null;
  faqCount: number | null;
  tableCount: number | null;
  createdAt: string;
  startedAt: string | null;
  updatedAt: string;
  completedAt: string | null;
  interruptedAt: string | null;
  currentDepth: number | null;
  currentSequence: number | null;
  reportAvailable: boolean;
  documentsAvailable: boolean;
  mayBePartial: boolean;
  artifacts: WebsiteCrawlArtifacts | null;
  report: WebsiteCrawlReport | null;
  documents: WebsiteCrawlPageResult[];
  error: string | null;
}

export interface WebsiteCrawlHistoryItem {
  crawlId: string;
  baseUrl: string;
  normalizedUrl: string;
  status: WebsiteCrawlStatus;
  currentUrl: string | null;
  discoveredCount: number;
  queuedCount: number;
  processedCount: number;
  successfulCount: number;
  failedCount: number;
  skippedCount: number;
  duplicateCount: number;
  documentCount: number;
  parsedDocumentCount?: number | null;
  ocrReadCount?: number | null;
  ocrDocumentCount?: number | null;
  skippedBreakdown?: Record<string, number> | null;
  skipBreakdown?: Record<string, number> | null;
  skipReasons?: Record<string, number> | null;
  duplicateBreakdown?: Record<string, number> | null;
  duplicatesBreakdown?: Record<string, number> | null;
  sectionCount: number;
  faqCount: number;
  tableCount: number;
  createdAt: string;
  startedAt: string | null;
  updatedAt: string;
  completedAt: string | null;
  interruptedAt: string | null;
  currentDepth: number | null;
  currentSequence: number | null;
  reportAvailable: boolean;
  documentsAvailable: boolean;
  mayBePartial: boolean;
  artifacts: WebsiteCrawlArtifacts | null;
  error: string | null;
}

export interface WebsiteCrawlHistoryResponse {
  items: WebsiteCrawlHistoryItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface WebsiteCrawlDocumentsResponse {
  items: WebsiteCrawlPageResult[];
  total: number;
  limit: number;
  offset: number;
}

/** Structured error body returned by the Spring crawler endpoints. */
export interface WebsiteCrawlErrorResponse {
  success: boolean;
  code: string;
  message: string;
}

/** Response body for POST /api/v1/chat/website-knowledge/jsonl/import. */
export interface WebsiteKnowledgeImportResponse {
  importId: string;
  websiteUrl: string;
  normalizedWebsiteUrl: string;
  sourceName: string | null;
  originalFilename: string | null;
  status: 'PROCESSING' | 'COMPLETED' | 'FAILED' | string;
  pagesRead: number;
  pagesInserted: number;
  chunksCreated: number;
  faqsCreated: number;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface WebsiteCrawlQaListResponse {
  crawlId: string;
  qaCount: number;
  filename: string;
  downloadUrl: string;
  generatedAt: string;
}

/** Client-side view model for one crawl job tracked by the page. */
export interface WebsiteCrawlJob {
  crawlId: string;
  baseUrl: string;
  status: WebsiteCrawlStatus;
  createdAt: string;
  detail: WebsiteCrawlStatusResponse | null;
  error: string | null;
}

export function isTerminalStatus(status: WebsiteCrawlStatus): boolean {
  return TERMINAL_CRAWL_STATUSES.includes(status);
}
